import { createContext, useCallback, useContext, useRef, useEffect, useState } from "react";
import socketIOClient from "socket.io-client";
import { AdminContext, ChatType } from "./adminContext";
import { Socket } from "dgram";
import { NotificationContext } from "./notificationContext";
import { useRequest } from "ahooks";
import { readChatByAdmin } from "helpers/backend_helper";
import { Chats } from "./chatsContext";

const configUrl = {
  socketUrl: process.env.NODE_ENV === "production" ? "https://c2c-event.goldapp.ru" : "https://c2c-event.goldapp.ru",
};

export type ContextProps = {
  socket: Socket;
  isConnectedSocket: boolean;
  chatIdRef: React.MutableRefObject<string>;
  online: { clientId: string; online: boolean };
  currentChatJoinedID: React.MutableRefObject<string>;
  chats: Chats;
  joinChatAdmin: (chatId: string) => Promise<any>;
  leaveChatAdmin: (chatId?: string, notLeaveFromIdChat?: string) => void;
  subscribeToUserOnliness: (userCountryId: string) => void;
  sendMessageSupport: (content: string, chatId: string) => Promise<any>;
  setOnline: React.Dispatch<React.SetStateAction<{ clientId: string; online: boolean }>>;
  setUserChats: React.Dispatch<React.SetStateAction<Chats>>;
};

export const REQUESTS_PER_PAGE = 8;
const FILE_FAILED_NOTICE_GAP_MS = 5000;
export const SocketContext = createContext({} as ContextProps);
export const SocketProvider = ({ children }: { children: JSX.Element }) => {
  const { localCountry, setChats } = useContext(AdminContext);
  const [chats, setUserChats] = useState<Chats | null>(null);
  const [online, setOnline] = useState({ clientId: "", online: false });
  const countryIsoCode = localCountry?.iso.toUpperCase();
  const access_token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
  const [socket, setSocket] = useState(null);
  const chatIdRef = useRef("");
  const [isConnectedSocket, setIsConnectedSocket] = useState(false);
  const currentChatJoinedID = useRef("");
  const joinedChats = useRef<string[]>([]);
  const lastFileFailedNoticeAt = useRef(0);
  const { openNotificationWithIcon } = useContext(NotificationContext);

  const { runAsync: readChat } = useRequest(
    (messageId: number) => {
      return readChatByAdmin(currentChatJoinedID.current, messageId, localCountry?.iso);
    },
    {
      ready: !!currentChatJoinedID.current,
      refreshDeps: [localCountry?.iso],
      manual: true,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || "something went wrong");
      },
    },
  );

  const joinChatAdmin = (chatId: string) => {
    return new Promise((res) => {
      if (joinedChats.current.includes(chatId)) {
        res(true);
        return;
      }
      socket.emit("joinSupportChat-admin", { chatId, countryIsoCode }, (response) => {
        joinedChats.current.push(chatId);
        console.log("ADMIN JOINED TO SUPPORT CHAT:", response);
        res(response);
      });
    });
  };

  const leaveChatAdmin = (chatId?: string, notLeaveFromChatId?: string) => {
    if (!chatId) {
      joinedChats.current.forEach((chatId) => {
        if (notLeaveFromChatId && chatId === notLeaveFromChatId) return;

        socket.emit("leaveSupportChat-admin", { chatId, countryIsoCode }, (response) => {
          console.log("ADMIN LEFT SUPPORT CHAT:", chatId, response);
          joinedChats.current = joinedChats.current.filter((id) => id !== chatId);
        });
      });
      return;
    }
    if (!joinedChats.current.includes(chatId)) {
      return;
    }
    socket.emit("leaveSupportChat-admin", { chatId, countryIsoCode }, (response) => {
      console.log("ADMIN LEFT SUPPORT CHAT:", chatId, response);
      joinedChats.current = joinedChats.current.filter((id) => id !== chatId);
    });
  };

  const sendMessageSupport = (content: string, chatId: string) =>
    new Promise((res) => {
      socket.emit("sendMessage-admin", { chatId, content, countryIsoCode }, (response) => {
        res(response);
        console.log("ADMIN SEND MESSAGE TO SUPPORT CHAT RES:", response);
      });
    });

  const subscribeToUserOnliness = useCallback(
    (userCountryId: string) => {
      socket.emit("subscribeToUserOnliness", { countryIsoCode, userCountryId: userCountryId });
    },
    [socket, countryIsoCode],
  );

  useEffect(() => {
    if (!access_token) return;
    const socketConnector = socketIOClient(configUrl.socketUrl, {
      path: "/socket.io/",
      transports: ["polling"],
      extraHeaders: {
        Authorization: `Bearer ${access_token}`,
        "Accept-Language": "EN",
      },
    });

    setSocket(socketConnector);

    socketConnector.on("connect", () => {
      console.log("socket connected");
      setIsConnectedSocket(true);
    });

    socketConnector.on("online", (message) => {
      console.log("online", message);
      setOnline(message);
    });

    socketConnector.on("newMessage", (message: any) => {
      console.log(message);
      if (message.chatId === currentChatJoinedID.current) {
        readChat(message.id);
        const addMsgToChat = (chat: string) => {
          setChats((prev) => {
            prev[chat].messages.unshift({ grouping_key: message.id, messages: [message] });
            return { ...prev, [chat]: prev[chat] };
          });
        };
        if (message.chatType === ChatType.SUPPORT) {
          addMsgToChat("supportChat");
        } else if (
          (message.chatType === ChatType.RENT || message.chatType === "PERSONAL") &&
          message.chatId === chatIdRef.current
        ) {
          addMsgToChat("debateChat");
        }
      } else {
        setUserChats((prev) => {
          const sliced = prev.chats.slice();
          const chat = sliced.find((chat) => chat.id === message.chatId);
          if (chat) {
            chat.unreadMessages = (chat.unreadMessages ?? 0) + 1;
          }
          return { chats: sliced, count: prev.count };
        });
      }
    });
    // Фоновая обработка фото провалилась уже после отправки
    socketConnector.on("fileProcessingFailed", () => {
      // Несколько фото пачки падают отдельными событиями — показываем одно уведомление
      const now = Date.now();
      if (now - lastFileFailedNoticeAt.current < FILE_FAILED_NOTICE_GAP_MS) return;
      lastFileFailedNoticeAt.current = now;
      openNotificationWithIcon("error", "Error while loading some files. Try again.");
    });
    socketConnector.on("newFileMessage", (message) => {
      if (message.chatId === currentChatJoinedID.current) {
        // Как в истории на бэке: группа = COALESCE(groupId, id)
        const groupingKey = message.files[0].groupId ?? message.files[0].id;
        setChats((prev) => {
          if (!prev?.selected?.type || prev?.selected?.chatId !== message.chatId) return prev;
          const chat = prev[prev.selected.type];
          // Готовое фото может уже прийти с перезапрошенной историей
          const files = message.files
            .filter((file) => !chat.messages.some((group) => group.messages?.some((m) => m.id === file.id)))
            .map((file) => ({ ...message, ...file }));
          if (!files.length) return prev;
          // Фото обрабатываются по одному и приходят отдельными событиями,
          // поэтому докладываем их в уже показанную группу пачки
          const isExistingGroup = chat.messages.some((group) => group.grouping_key === groupingKey);
          const messages = isExistingGroup
            ? chat.messages.map((group) =>
                group.grouping_key === groupingKey ? { ...group, messages: [...group.messages, ...files] } : group,
              )
            : [{ grouping_key: groupingKey, messages: files }, ...chat.messages];
          return { ...prev, [prev.selected.type]: { ...chat, messages } };
        });
      } else {
        setUserChats((prev) => {
          const sliced = prev.chats.slice();
          const chat = sliced.find((chat) => chat.id === message.chatId);
          if (chat) {
            chat.unreadMessages = (chat.unreadMessages ?? 0) + 1;
          }
          return { chats: sliced, count: prev.count };
        });
      }
    });

    return () => {
      socketConnector.disconnect();
    };
  }, [access_token]);

  return (
    <SocketContext.Provider
      value={{
        joinChatAdmin,
        chats,
        setUserChats,
        sendMessageSupport,
        socket,
        chatIdRef,
        leaveChatAdmin,
        subscribeToUserOnliness,
        online,
        setOnline,
        isConnectedSocket,
        currentChatJoinedID,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
